import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit, viewChild } from '@angular/core';
import { MatAccordion, MatExpansionModule } from '@angular/material/expansion';
import { MatTableModule } from '@angular/material/table';
import { ProductService } from '../product.service';
import { skip, take } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { ProductExpansionPanelTemplateComponent } from './product-expansion-panel-template/product-expansion-panel-template.component';
import { DialogResultOutput, ProductCard, ProductGuide, ProductHotel, ProductTicket } from '../product.model';
import { ProductMatTableTemplateComponent } from './product-mat-table-template/product-mat-table-template.component';
import { ProductApiService } from '../product.api.service';
import { cardsDialogConfig, cardsTableTemplate } from './config-models/cards.model';
import { hotelsDialogConfig, hotelsTableTemplate } from './config-models/hotels.model';
import { guidesDialogConfig, guidesTableTemplate } from './config-models/guides.model';
import { ticketsDialogConfig, ticketsTableTemplate } from './config-models/tickets.model';

@Component({
	selector: 'app-product-organization-config',
	standalone: true,
	imports: [
		MatExpansionModule,
		MatTableModule,
		MatButtonModule,
		ProductExpansionPanelTemplateComponent,
		ProductMatTableTemplateComponent,
	],
	templateUrl: './product-organization-config.component.html',
	styleUrl: './product-organization-config.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductOrganizationConfigComponent implements OnInit {
	cards: ProductCard[] = [];
	guides: ProductGuide[] = [];
	tickets: ProductTicket[] = [];
	hotels: ProductHotel[] = [];

	readonly accordion = viewChild.required(MatAccordion);

	// cards models
	readonly cardsDialogConfig = cardsDialogConfig;
	readonly cardsTableTemplate = cardsTableTemplate;

	// hotels models
	readonly hotelsDialogConfig = hotelsDialogConfig;
	readonly hotelsTableTemplate = hotelsTableTemplate;

	// guides models
	readonly guidesDialogConfig = guidesDialogConfig;
	readonly guidesTableTemplate = guidesTableTemplate;

	// tickets models
	readonly ticketsDialogConfig = ticketsDialogConfig;
	readonly ticketsTableTemplate = ticketsTableTemplate;

	private readonly productService = inject(ProductService);
	private readonly apiService = inject(ProductApiService);
	private readonly cdr = inject(ChangeDetectorRef);

	ngOnInit(): void {
		// skip(1) — skips the first emission from dataLoaded$ because the initial value
		// of this.productService.data contains mockData with default values.
		// We only care about the actual response coming from the backend.
		//
		// take(1) — limits the subscription to a single emission since, due to legacy code
		// in other product components, dataLoaded$ may emit up to three times.
		this.productService.dataLoaded$.pipe(skip(1), take(1)).subscribe(() => {
			this.cards = this.productService.data.cards;
			this.guides = this.productService.data.guides;
			this.hotels = this.productService.data.hotels;
			this.tickets = this.productService.data.tickets;
		});
	}

	updateProduct(result: DialogResultOutput): void {
		let payload = {};
		Object.entries(result).forEach(([key, value]) => {
			const normalizeDestination = (obj: ProductGuide) => ({
				...obj,
				...(obj?.destination?.id ? { destination: obj.destination.id } : {}),
			});

			const item = {
				[key]: [
					...this.productService.data[key].filter((item) => item.id !== value?.id).map(normalizeDestination),
					normalizeDestination(value as ProductGuide),
				],
			};

			if (!item) {
				return;
			}

			payload = { ...payload, ...item };
		});

		this.apiService.patchProduct(this.productService.data.id, payload).subscribe((updatedProduct) => {
			Object.keys(payload).forEach((key) => {
				this.productService.data[key] = updatedProduct[key];
				this[key] = updatedProduct[key];
			});

			this.cdr.detectChanges();
		});
	}
}
